// src/perm.ts —— TD-830 / G-10：GET /admin/me 权限位驱动的显隐与置灰（纯函数）。仅 UI 层；数据面由后端 bearer + policy 兜底（403/1501）
import type { SessionPolicy } from '../../shared/src/api/types';
import type { Dict } from '../../shared/src/dc';
import type { Page } from './state';

// backend/internal/authn/policy/policy.go Modules 逐字同序（含 camelCase agentWd；0047 末位追加 profit；0049 末位追加 broadcast）
export const MODULES = ['users', 'cards', 'tx', 'wallets', 'review', 'agents', 'commissions', 'agentWd', 'products', 'settings', 'tgbot', 'payout', 'orders', 'logs', 'cms', 'admins', 'cryptoPayments', 'cardSecret', 'profit', 'broadcast'] as const;
export type Module = (typeof MODULES)[number];
export type Bit = 'view' | 'edit' | 'approve' | 'export';

export interface Bits {
  view: boolean;
  edit: boolean;
  approve: boolean;
  export: boolean;
}

export interface AdminMe {
  account: string;
  name: string;
  role: string;
  permissions: Readonly<Record<string, Bits>>;
  must_change_password: boolean;
  totp_enabled: boolean;
  // GET /me 附带的告警铃与侧栏 logs 徽标计数（admincore.Badges；无 logs.view 时为 0）。与 shared AdminMe 同形。
  badges: Readonly<{ crypto_exceptions?:number; alerts_open: number; webhooks_failed: number }>;
  // 2026-10-15 空闲超时 / 最长在线（adminIdleMinutes / adminMaxHours）；与 shared AdminMe.session 同形，旧后端缺省时按默认值计时
  session?: Readonly<SessionPolicy>;
  // 0049 个人设置「Telegram 数字 ID」（接收广告推送预览）。后端 /me 若回显则用于表单预填；缺省 = 未知（表单留空）
  telegram_id?: string | null;
}

export const SUPER_ADMIN_ROLE = 'Super admin'; // authn/guard.go:246

// 02 §6.3：页 → 查看所需模块；'open' = 矩阵外全部角色只读（dashboard / upstream）；'super' = 仅 Super admin（roles）
export const PAGE_MODULE = {
  cryptoPayments:'cryptoPayments',dashboard: 'open', profit: 'profit', users: 'users', cards: 'cards', cardActions: 'cards', tx: 'tx', wallets: 'wallets', review: 'review', treasury: 'wallets',
  agents: 'agents', agentUsers: 'agents', commissions: 'commissions', agentWd: 'agentWd',
  products: 'products', settings: 'settings', tgbot: 'tgbot', broadcast: 'broadcast',
  payout: 'payout', orders: 'orders', budgets: 'wallets', upstreamCosts: 'wallets', upstream: 'open', logs: 'logs', alerts: 'logs',
  cms: 'cms', admins: 'admins', roles: 'super', audit: 'logs',
} as const satisfies Readonly<Record<Page, Module | 'open' | 'super'>>;

// 已下线页（用户 2026-10-15：新收付架构下不再需要的功能隐藏）：对任何角色（含超管）不可见——侧栏不列、登录落地与看板下钻
// 不落到这些页。页定义与只读接口保留以便回溯；清单与理由见 docs/reviews/2026-10-15-legacy-feature-cleanup.md。
//   treasury（C-25 金库归集登记）：固定共享收款地址由业主自有钱包持有、无平台派生地址可归集；负债 / 净入金已在 C-02 看板展示；
//   登记接口 POST /api/admin/treasury/sweeps 已下线，历史记录仍可经 GET /api/admin/treasury/sweeps 只读导出。
export const RETIRED_PAGES: ReadonlySet<Page> = new Set<Page>(['treasury']);

export function can(me: AdminMe | null, module: Module, bit: Bit): boolean {
  return !!me?.permissions[module]?.[bit];
}

export function canSee(me: AdminMe | null, page: Page): boolean {
  if (!me || RETIRED_PAGES.has(page)) return false;
  const m: Module | 'open' | 'super' = PAGE_MODULE[page];
  if (m === 'open') return true;
  if (m === 'super') return me.role === SUPER_ADMIN_ROLE;
  return can(me, m, 'view');
}

// 有页面承载的矩阵模块（cardSecret 等纯动作位模块无页面，不计入「有可进入页面」判定）
const PAGE_MODULES: ReadonlySet<string> = new Set<string>(Object.values(PAGE_MODULE));

// 是否持有任一矩阵模块的 view（G-10 第 1 条「全部无权限」判定；矩阵外的 dashboard / upstream 不计；仅 cardSecret.view 不算有模块）
export function hasAnyModule(me: AdminMe | null): boolean {
  return !!me && (me.role === SUPER_ADMIN_ROLE || MODULES.some((m) => PAGE_MODULES.has(m) && can(me, m, 'view')));
}

export interface Gate {
  disabled: boolean;
  title: string;
}

// 位名：edit → t.permEdit「编辑 / edit」、approve → t.permApprove「审核 / approve」（原型 t.edit「Edit」/ t.approve「通过」为按钮文案，不作位名；en 统一小写）
const BIT_LABEL: Readonly<Record<'edit' | 'approve', string>> = { edit: 'permEdit', approve: 'permApprove' };

// G-10 第 2 条：无 edit / approve → 保留置灰 + title「无权限：需要「{module}·{bit}」权限」
export function gate(me: AdminMe | null, t: Dict, module: Module, bit: 'edit' | 'approve'): Gate {
  if (can(me, module, bit)) return { disabled: false, title: '' };
  const tpl = typeof t['noPermHint'] === 'string' ? t['noPermHint'] : 'noPermHint';
  const label = (k: string): string => {
    const v = t[k];
    return typeof v === 'string' ? v : k;
  };
  return { disabled: true, title: tpl.replace('{module}', label(module)).replace('{bit}', label(BIT_LABEL[bit])) };
}

// 置灰样式：原样式串后追加（后写覆盖前写），不改原型按钮尺寸
export function gatedStyle(style: string, g: Gate): string {
  return g.disabled ? `${style};opacity:.5;cursor:not-allowed` : style;
}
